import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Heading from '@react/react-spectrum/Heading';
import Button from '@react/react-spectrum/Button';
import Search from '@react/react-spectrum/Search';
import Dialog from '@react/react-spectrum/Dialog';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from '@material-ui/core';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';

import './ClosedDiscountsTab.css';
import { OdDiscount } from '../../../models/OdDiscount';
import { deleteOdDiscount, getOdDiscounts } from '../../../providers/OdDiscountsAPI';
import CreateOdDiscountDialog from './CreateOdDiscountDialog';

interface ClosedDiscountsTabProps {
    readonly partnerId: string;
}

function formatDateTime(iso?: string): string {
    if (!iso) return '';
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString(undefined, { timeZone: 'UTC' });
}

function ClosedDiscountsTab({ partnerId }: ClosedDiscountsTabProps) {
    const [discounts, setDiscounts] = useState<OdDiscount[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(true);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [deleteTargetCode, setDeleteTargetCode] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchDiscounts = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getOdDiscounts(partnerId);
            if (response.httpstatus === 200) {
                setDiscounts(response.data || []);
            } else {
                setErrorMessage(response.message || 'Failed to fetch closed discount codes.');
            }
        } catch (e) {
            const err = e as any;
            setErrorMessage(err?.message || 'Failed to fetch closed discount codes.');
        } finally {
            setIsLoading(false);
        }
    }, [partnerId]);

    useEffect(() => {
        if (partnerId) {
            fetchDiscounts();
        }
    }, [fetchDiscounts, partnerId]);

    const filteredDiscounts = useMemo(() => {
        const q = appliedSearchQuery.trim().toLowerCase();
        if (!q) return discounts;
        return discounts.filter(
            (d) =>
                d.code.toLowerCase().includes(q) ||
                d.customerId.toLowerCase().includes(q) ||
                d.promotionId.toLowerCase().includes(q) ||
                d.label.toLowerCase().includes(q) ||
                d.status.toLowerCase().includes(q)
        );
    }, [discounts, appliedSearchQuery]);

    const onSearchInputChange = (value: string) => {
        setSearchInput(value);
        setIsSearching(true);
        // The Search component's built-in clear ("x") icon fires this same onChange
        // with an empty value — treat that as an implicit reset so stale filtered
        // results don't linger once the box is empty.
        if (value === '') {
            setAppliedSearchQuery('');
        }
    };

    const onSearchButtonClick = () => {
        // If searching, apply the typed text as the filter. Otherwise, clear it.
        if (isSearching) {
            setAppliedSearchQuery(searchInput);
        } else {
            setSearchInput('');
            setAppliedSearchQuery('');
        }
        setIsSearching(!isSearching);
    };

    const handleCreated = () => {
        setIsCreateDialogOpen(false);
        fetchDiscounts();
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTargetCode) return;
        setIsDeleting(true);
        try {
            const response = await deleteOdDiscount(partnerId, deleteTargetCode);
            if (response.httpstatus !== 204 && response.httpstatus !== 200) {
                setErrorMessage(response.message || 'This code no longer exists. Refresh the list.');
            }
        } catch (e) {
            const err = e as any;
            setErrorMessage(err?.message || 'Failed to delete closed discount code.');
        } finally {
            setIsDeleting(false);
            setDeleteTargetCode(null);
            fetchDiscounts();
        }
    };

    return (
        <div>
            <div className="closed-discounts-heading-container">
                <Heading variant="subtitle3">
                    Create a customer-specific Closed discount code.
                </Heading>
                <Button variant="cta" onClick={() => setIsCreateDialogOpen(true)}>
                    Create Closed Discount Code
                </Button>
            </div>
            <br />
            <hr className="closed-discounts-divider" />
            <br />
            <br />
            <div className="closed-discounts-search-label-box">Search for Flex Discounts</div>
            <div className="closed-discounts-search-container">
                <Search
                    className="closed-discounts-search-bar"
                    aria-label="Search"
                    placeholder="Search by code, customer ID, flex discount ID or status..."
                    value={searchInput}
                    onChange={(value) => onSearchInputChange(value)}
                    onSubmit={() => onSearchButtonClick()}
                />
                <Button className="closed-discounts-search-button" onClick={onSearchButtonClick}>
                    {isSearching ? 'Search' : 'Clear'}
                </Button>
            </div>
            <br />
            <br />
            <br />

            {isLoading ? (
                <CircularProgress size={20} className="closed-discounts-table-loading-spinner" />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="closed-discounts-table-head">Closed Discount Code</TableCell>
                                <TableCell className="closed-discounts-table-head">Customer ID</TableCell>
                                <TableCell className="closed-discounts-table-head">Template ID</TableCell>
                                <TableCell className="closed-discounts-table-head">Closed Discount Label</TableCell>
                                <TableCell className="closed-discounts-table-head">Expiry Date</TableCell>
                                <TableCell className="closed-discounts-table-head">Redemption Status</TableCell>
                                <TableCell className="closed-discounts-table-head">Delete</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDiscounts.map((discount) => (
                                <TableRow key={discount.id}>
                                    <TableCell>
                                        {discount.code}
                                        <IconButton
                                            className="copy-icon"
                                            onClick={() => navigator.clipboard.writeText(discount.code)}
                                        >
                                            <FileCopyOutlined className="tiny-icon" style={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{discount.customerId}</TableCell>
                                    <TableCell>{discount.templateId}</TableCell>
                                    <TableCell>{discount.label}</TableCell>
                                    <TableCell>{formatDateTime(discount.endDate)}</TableCell>
                                    <TableCell>{discount.status}</TableCell>
                                    <TableCell>
                                        <Button variant="warning" onClick={() => setDeleteTargetCode(discount.code)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredDiscounts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>No closed discount codes found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {isCreateDialogOpen && (
                <CreateOdDiscountDialog
                    isOpen={isCreateDialogOpen}
                    partnerId={partnerId}
                    onCreated={handleCreated}
                    onCancel={() => setIsCreateDialogOpen(false)}
                />
            )}

            <Dialog
                className="closed-discounts-delete-dialog"
                title="Delete Closed Discount Code"
                open={deleteTargetCode !== null}
                confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
                cancelLabel="Cancel"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTargetCode(null)}
            >
                Are you sure you want to delete code {deleteTargetCode}? This cannot be undone.
            </Dialog>

            <Dialog
                className="closed-discounts-error-dialog"
                title="Error"
                open={errorMessage !== ''}
                confirmLabel="OK"
                onConfirm={() => setErrorMessage('')}
            >
                {errorMessage}
            </Dialog>
        </div>
    );
}

export default ClosedDiscountsTab;
